/**
 * UPT-2096 (L2, web §5.1) — lecture des fichiers texte du dialogue d'import
 * (HTML et Markdown) : pas de DOM de rendu, pas de canvas ; la lecture passe par
 * `arrayBuffer()` (simulable en test). Partagé par le dialogue et par « Ajouter
 * les sources manquantes ».
 */
import { PROTOTYPE_LIMITS, type PrototypeDocumentRole } from '../data/contracts';
import { guessSpecTitle } from '../specs/markdownImport';
import {
  decodeHtml,
  decodeMarkdown,
  guessDocumentRole,
  splitMarkdown,
  utf8ByteLength,
  type PreparedHtml,
  type PreparedMarkdown,
} from './files';

/** Fichier lisible (File en production, objet simulé en test). */
export interface ReadableFile {
  name: string;
  arrayBuffer: () => Promise<ArrayBuffer>;
}

export type PreparedHtmlResult =
  | { ok: true; html: PreparedHtml }
  | { ok: false; reason: 'size' };

/** HTML décodé ; la borne de taille s'applique APRÈS transcodage (octets UTF-8). */
export const prepareHtmlFile = async (file: ReadableFile): Promise<PreparedHtmlResult> => {
  const decoded = decodeHtml(new Uint8Array(await file.arrayBuffer()));
  if (utf8ByteLength(decoded.text) > PROTOTYPE_LIMITS.htmlMaxBytes) {
    return { ok: false, reason: 'size' };
  }
  return { ok: true, html: { fileName: file.name, text: decoded.text, fallback: decoded.fallback } };
};

export type PreparedMarkdownResult =
  | { ok: true; markdown: PreparedMarkdown; markdowns: PreparedMarkdown[] }
  | { ok: false; reason: 'markdown-encoding' | 'empty' };

/**
 * Document Markdown : UTF-8 strict (jamais de repli), texte vide ou blanc
 * refusé ; rôle proposé par `guessDocumentRole` selon les rôles déjà pris.
 */
export const prepareMarkdownFile = async (
  file: ReadableFile,
  existingRoles: readonly PrototypeDocumentRole[]
): Promise<PreparedMarkdownResult> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const decoded = decodeMarkdown(bytes);
  if (decoded === null) return { ok: false, reason: 'markdown-encoding' };
  if (decoded.text.trim() === '') return { ok: false, reason: 'empty' };
  // 23.10.2026 : un document plus gros que la borne est découpé en parties (`markdowns`, 1..n) ;
  // `markdown` reste la première partie (compatibilité). Une seule partie porte le rôle « prompt ».
  const parts = splitMarkdown(file.name, decoded.text);
  const role = guessDocumentRole(file.name, existingRoles);
  const encoder = new TextEncoder();
  const markdowns: PreparedMarkdown[] = parts.map((part, index) => ({
    fileName: part.fileName,
    text: part.text,
    bytes: parts.length === 1 ? bytes : encoder.encode(part.text),
    role: index === 0 ? role : 'notes',
  }));
  return { ok: true, markdown: markdowns[0], markdowns };
};

/** Titre proposé par le `<title>` d'un HTML (document inerte), tronqué à la borne du titre. */
export const htmlTitleOf = (text: string): string | null => {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const title = doc.title?.trim();
  return title ? title.slice(0, PROTOTYPE_LIMITS.titleMaxChars) : null;
};

/**
 * Titre proposé : `<title>` de la source 0, sinon premier titre `#` du premier
 * `.md` (nom sans extension à défaut), sinon nom de la première image (sans
 * extension) ; null sans fichier.
 */
export const suggestImportTitle = (files: {
  htmls: readonly PreparedHtml[];
  markdowns: readonly PreparedMarkdown[];
  imageNames: readonly string[];
}): string | null => {
  const fromHtml = files.htmls[0] ? htmlTitleOf(files.htmls[0].text) : null;
  if (fromHtml) return fromHtml;
  const markdown = files.markdowns[0];
  if (markdown) return guessSpecTitle(markdown.fileName, markdown.text).slice(0, PROTOTYPE_LIMITS.titleMaxChars);
  const image = files.imageNames[0];
  return image ? image.replace(/\.[^.]+$/, '').slice(0, PROTOTYPE_LIMITS.titleMaxChars) : null;
};

/**
 * Change le rôle d'un document : au plus un `prompt` — choisir `prompt` sur un
 * fichier repasse l'autre en `notes` (`switched` = nom du fichier repassé).
 */
export const setDocumentRole = (
  markdowns: readonly PreparedMarkdown[],
  index: number,
  role: PrototypeDocumentRole
): { markdowns: PreparedMarkdown[]; switched: string | null } => {
  let switched: string | null = null;
  const next = markdowns.map((markdown, position) => {
    if (position === index) return { ...markdown, role };
    if (role === 'prompt' && markdown.role === 'prompt') {
      switched = markdown.fileName;
      return { ...markdown, role: 'notes' as const };
    }
    return markdown;
  });
  return { markdowns: next, switched };
};
